import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

// redux slice
import {
	selectUserDetails,
	selectSelectedOrganization,
	selectOrganizationByInternalUser,
	setSelectedOrganizationByInternalUser,
	setSelectedOrganization,
	setOrganizationId,
	setOrganizationIdByInternalUser,
} from "../../features/auth/authSlice";

// helper utils
import { storageLoad, storageSave } from "../../utils/localStorage";

// custom components
import SecondarySelectLOLP from "../custom-select/secondary-select-lo-lp";

const OrganizationDropdown = ({ extraClass = "" }) => {
	const dispatch = useDispatch();
	const userDetails = useSelector(selectUserDetails);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const selectedOrganizationByInternal = useSelector(selectOrganizationByInternalUser);
	console.log("Selected Org from Redux:", selectedOrganizationByInternal);
	const location = useLocation();
	const hasClearedSelection = useRef(false);
	const firstSelectionCompleteKey = `new-loan-org-selection-complete-${userDetails?.username || "anonymous"}`;
	const isNewLoanPage = location.pathname === '/new-loan';
	const isExecutiveOrManager =
		userDetails?.user_roles?.includes("executive") ||
		userDetails?.user_roles?.includes("admin") ||
		userDetails?.user_roles?.includes("manager") ||
		userDetails?.user_roles?.includes("AE") ||
		userDetails?.user_roles?.includes("AM") ||
		userDetails?.user_roles?.includes("PA") ||
		userDetails?.user_roles?.includes("assistant");
	// NEW: fallback logic
	const organizationsData =
		userDetails?.organizations && userDetails.organizations.length > 0
			? userDetails.organizations
			: (userDetails?.snowFlakeOrganizations || []).map((org, index) => ({
					id: org.tpo_id || index,
					name: org.company_name,
					login_enabled: true,
					loan_channel: null,
					raw: org
			  }));

	// Set initial selected organization from user details or Redux state.
	useEffect(() => {
		if (organizationsData && organizationsData.length > 0) {
			const enabledOrgs = organizationsData.filter(org => org.login_enabled !== false);

			const isNewLoanPage = location.pathname === '/new-loan';
			const hasCompletedFirstSelection = storageLoad(firstSelectionCompleteKey) === true;
			
			if (isNewLoanPage && enabledOrgs.length > 1 && !hasCompletedFirstSelection) {
				if (!hasClearedSelection.current) {
					dispatch(setSelectedOrganization(null));
					dispatch(setOrganizationId(null));
					hasClearedSelection.current = true;
				}
				return;
			} else if (!isNewLoanPage) {
				hasClearedSelection.current = false;
			}

			if(!isExecutiveOrManager){
				if (!selectedOrganization || selectedOrganization === "") {
					const savedOrg = storageLoad("selected-organization");
					const savedOrgIsValid = savedOrg && enabledOrgs.some(org => org.name === savedOrg);
					const savedOrgID = storageLoad("selected-organization-id");
					
					if (savedOrgIsValid) {
						dispatch(setSelectedOrganization({name: savedOrg, id: savedOrgID}));
						dispatch(setOrganizationId(savedOrgID));
						console.log("Test org id1", savedOrgID);
						console.log("Test org id1", savedOrg);
					} else {
						// UPDATED: handle fallback case
						const defaultOrg =
							userDetails?.organizations?.length > 0
								? enabledOrgs.find(org => org.name === userDetails.organization_name)
								: enabledOrgs[0];

						const orgName = defaultOrg?.name || enabledOrgs[0]?.name || "";

						if (orgName) {
							const organization = organizationsData.find(org => org.name === orgName);
							dispatch(setSelectedOrganization({name: orgName, id: organization.id}));
							dispatch(setOrganizationId(organization.id));
							console.log("Test org id2", organization.id);
							console.log("Test org id2", orgName);
						}
					}
				}
			} else {

				if (!selectedOrganizationByInternal || selectedOrganizationByInternal === "") {
					const savedOrg = storageLoad("selected-organization-internal");
					const savedOrgIsValid = savedOrg && enabledOrgs.some(org => org.name === savedOrg);
					const savedOrgID = storageLoad("selected-organization-id-internal");
					
					if (savedOrgIsValid) {
						dispatch(setSelectedOrganizationByInternalUser({name: savedOrg, id: savedOrgID}));
						dispatch(setOrganizationIdByInternalUser(savedOrgID));
						console.log("Test org id1 - internal", savedOrgID);
						console.log("Test org id1 - internal", savedOrg);
					} else {
						
						const orgName = enabledOrgs[0]?.name || "";

						if (orgName) {
							const organization = organizationsData.find(org => org.name === orgName);
							dispatch(setSelectedOrganizationByInternalUser({name: orgName, id: organization.id}));
							dispatch(setOrganizationIdByInternalUser(organization.id));
							console.log("Test org id2 - internal", organization.id);
							console.log("Test org id2 - internal", orgName);
						}
					}
				}

			}
		}
	}, [dispatch, firstSelectionCompleteKey, location.pathname, selectedOrganization, userDetails, organizationsData, selectedOrganizationByInternal]);

	const handleOrganizationChange = (event) => {
		if(!isExecutiveOrManager){
			const selectedOrgName = event.target.value;

			if (!selectedOrgName && selectedOrganization) {
				return;
			}

			if (!selectedOrgName) {
				dispatch(setSelectedOrganization(null));
				dispatch(setOrganizationId(null));
				return;
			}

			const organization = organizationsData.find(org => org.name === selectedOrgName);
			if (!organization) {
				return;
			}

			storageSave(firstSelectionCompleteKey, true);
			dispatch(setSelectedOrganization({name: selectedOrgName, id: organization.id}));
			dispatch(setOrganizationId(organization.id));
		}
		else {
			const selectedOrgName = event.target.value;

			if (!selectedOrgName && selectedOrganizationByInternal) {
				return;
			}

			if (!selectedOrgName) {
				dispatch(setSelectedOrganizationByInternalUser(null));
				dispatch(setOrganizationIdByInternalUser(null));
				return;
			}

			const organization = organizationsData.find(org => org.name === selectedOrgName);
			if (!organization) {
				return;
			}

			storageSave(firstSelectionCompleteKey, true);
			dispatch(setSelectedOrganizationByInternalUser({name: selectedOrgName, id: organization.id}));
			dispatch(setOrganizationIdByInternalUser(organization.id));
		}
	};

	//  UPDATED: use fallback data
	if (!organizationsData || organizationsData.length === 0) {
		return null;
	}

	const enabledOrgs = organizationsData.filter(org => org.login_enabled !== false);

	if (enabledOrgs.length === 0) {
		return null;
	}

	const isDisabled = enabledOrgs.length === 1;

	const organizationOptions = enabledOrgs.map((org) => ({
		value: org.name,
		label: org.name,
		loan_channel: org.loan_channel
	}));

	const dropdownValue = selectedOrganization || selectedOrganizationByInternal || "";

	return (
		<SecondarySelectLOLP
			id="organization-dropdown"
			displayEmpty={false}
			value={dropdownValue}
			handleChange={handleOrganizationChange}
			disabled={isDisabled}
			options={organizationOptions}
			fullWidth={true}
			extraClass={`organization-dropdown ${extraClass} ${isNewLoanPage ? 'new-loan-org-dropdown' : ''}`.trim()}
		/>
	);
};

export default OrganizationDropdown;
